import React, { useEffect, useMemo } from "react";
import "../sass/components/modalParcelas.scss";

const parseNumeroBR = (valor) => {
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : 0;
  const texto = String(valor ?? "0").trim();
  if (!texto) return 0;

  const normalizado = texto.includes(",")
    ? texto.replace(/\./g, "").replace(",", ".")
    : texto;
  const numero = Number(normalizado.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(numero) ? numero : 0;
};

const moedaBR = (valor) =>
  parseNumeroBR(valor).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const parseDataBR = (data) => {
  if (!data) return null;
  const partes = String(data).split("/");
  if (partes.length !== 3) return null;

  const [dia, mes, ano] = partes.map(Number);
  const dt = new Date(ano, mes - 1, dia);
  dt.setHours(0, 0, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const getStatusInfo = (parcela) => {
  const situacao = String(parcela?.situacao || "").trim().toUpperCase();
  const desc = String(parcela?.situacaoDescricao || "").trim();

  if (parcela?.atrasado) return { label: "Atrasada", className: "status-atrasada" };
  if (situacao === "P") return { label: desc || "Pendente", className: "status-pendente" };
  if (["Q", "L"].includes(situacao)) return { label: desc || "Paga", className: "status-paga" };
  return { label: desc || "Outro", className: "status-outro" };
};

const getPeriodoInfo = (parcela) => {
  const periodo = String(parcela?.periodo || "").toUpperCase();
  if (periodo === "ANTERIOR") return { label: "Anterior", className: "periodo-anterior" };
  if (periodo === "FUTURA") return { label: "Futura", className: "periodo-futura" };

  const dt = parseDataBR(parcela?.vencimento);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  return dt && dt <= hoje
    ? { label: "Anterior", className: "periodo-anterior" }
    : { label: "Futura", className: "periodo-futura" };
};

const ModalParcelas = ({ onClose, parcelas }) => {
  const lista = useMemo(
    () => (Array.isArray(parcelas?.parcelas) ? parcelas.parcelas : []),
    [parcelas?.parcelas],
  );

  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const resumo = useMemo(() => {
    const pagas = lista.filter((p) => ["Q", "L"].includes(String(p.situacao || "").toUpperCase())).length;
    const pendentes = lista.filter((p) => String(p.situacao || "").toUpperCase() === "P").length;
    const atrasadas = lista.filter((p) => p.atrasado).length;

    return {
      total: lista.length,
      atrasadas,
      pagas,
      pendentes,
      totalGeral: parcelas?.totalGeral || "0,00",
      totalAtrasado: parcelas?.totalAtrasado || "0,00",
    };
  }, [lista, parcelas?.totalGeral, parcelas?.totalAtrasado]);

  return (
    <div className="modal-parcelas-backdrop" onMouseDown={onClose}>
      <section
        className="modal-parcelas"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-parcelas-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal-parcelas-header">
          <div>
            <span className="modal-parcelas-eyebrow">Financeiro</span>
            <h2 id="modal-parcelas-title">Parcelas do cliente</h2>
            <p>Últimas cinco anteriores e próximas cinco futuras.</p>
          </div>
          <button type="button" className="modal-parcelas-close" onClick={onClose} aria-label="Fechar">×</button>
        </header>

        <div className="modal-parcelas-kpis">
          <article><span>Total exibido</span><strong>{resumo.total}</strong></article>
          <article><span>Pendentes</span><strong>{resumo.pendentes}</strong></article>
          <article className={resumo.atrasadas > 0 ? "danger" : ""}><span>Atrasadas</span><strong>{resumo.atrasadas}</strong></article>
          <article><span>Pagas</span><strong>{resumo.pagas}</strong></article>
        </div>

        <div className="modal-parcelas-table-wrap">
          <table className="modal-parcelas-table">
            <thead><tr><th>Vencimento</th><th>Período</th><th>Status</th><th>Valor</th><th>Parcela</th></tr></thead>
            <tbody>
              {lista.length === 0 ? (
                <tr><td colSpan={5} className="modal-parcelas-empty">Nenhuma parcela encontrada para este cliente.</td></tr>
              ) : lista.map((parcela, index) => {
                const status = getStatusInfo(parcela);
                const periodo = getPeriodoInfo(parcela);
                return (
                  <tr key={`${parcela.numParcela || index}-${parcela.vencimento || index}`}>
                    <td><strong>{parcela.vencimento || "—"}</strong></td>
                    <td><span className={`periodo-badge ${periodo.className}`}>{periodo.label}</span></td>
                    <td><span className={`parcela-status ${status.className}`}>{status.label}</span></td>
                    <td className="valor-cell">{moedaBR(parcela.valor)}</td>
                    <td className="parcela-cell">{parcela.numParcela || "—"}{parcela.numOrigem ? <small>Origem: {parcela.numOrigem}</small> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <footer className="modal-parcelas-footer">
          <div><span>Total geral</span><strong>{moedaBR(resumo.totalGeral)}</strong></div>
          <div className={resumo.atrasadas > 0 ? "danger" : ""}><span>Total atrasado</span><strong>{moedaBR(resumo.totalAtrasado)}</strong></div>
        </footer>
      </section>
    </div>
  );
};

export default ModalParcelas;
